import { useState, useEffect } from 'react';
import { useStores } from '../../components/UserJourny/FirstUserJourney/useStores';
import useCreateStoreKnowledge from '../../hooks/useCreateStoreKB';
import { IStoreKnowledgeRequest } from '../../schemas/apis/interface';
import { NewFaqSetupUI } from './NewFaqSetupUI';
import QuestionEditDrawer from './QuestionEditDrawer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deleteStoreKnowledgeApi,
  editStoreKnowledgeApi,
  deleteKnowledgeBaseFile,
} from '../../utils/apis';
import { useAlert } from '../../hooks/useAlert';
import { useDispatch } from 'react-redux';
import { setStepStatus } from '../../utils/redux/slices/generalSupportSlice';
import { useUser } from '../../hooks/useUser';

interface Props {
  onNext: () => void;
  onPrev: () => void;
}

const NewFaqSetup = ({ onNext, onPrev }: Props) => {
  const { showAlert } = useAlert();
  const { storesList, storesListLoading } = useStores();
  const { createStoreKnowledge, isPending } = useCreateStoreKnowledge();
  const dispatch = useDispatch();
  const { user } = useUser();

  const queryClient = useQueryClient();

  const [isQuestionnaireDrawerOpen, setIsQuestionnaireDrawerOpen] =
    useState(false);
  const [selectedStore, setSelectedStore] = useState<any>({});
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [questionIndex, setQuestionIndex] = useState<number>(-1);
  const [deletingQuestionIds, setDeletingQuestionIds] = useState<number[]>([]);

  const { mutate: editStoreKnowledge, isPending: isEditPending } = useMutation({
    mutationFn: editStoreKnowledgeApi,
    onSuccess: () => {
      setIsEditDrawerOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['stores'],
      });
    },
    onError: (error: any) => {
      setIsEditDrawerOpen(false);
      showAlert(error.response.data.message, 'error');
    },
  });

  const { mutate: deleteStoreKnowledge, isPending: isDeletePending } =
    useMutation({
      mutationFn: deleteStoreKnowledgeApi,
      onMutate: variables => {
        setDeletingQuestionIds(prev => [...prev, Number(variables.id)]);
      },
      onSuccess: (_, variables) => {
        setDeletingQuestionIds(prev =>
          prev.filter(id => id !== Number(variables.id))
        );
        queryClient.invalidateQueries({
          queryKey: ['stores'],
        });
        showAlert('Question deleted successfully', 'success');
      },
      onError: (error: any, variables) => {
        setDeletingQuestionIds(prev =>
          prev.filter(id => id !== Number(variables.id))
        );
        showAlert(error.response.data.message, 'error');
      },
    });

  const handleCreateQuestionnaire = (store: any) => {
    // Get the current number of questions for this store
    const existingQuestionsCount = store.storeknowledgeBase?.qna?.length || 0;
    setSelectedStore({
      ...store,
      existingQuestionsCount, // Pass this to the drawer
    });
    setIsQuestionnaireDrawerOpen(true);
  };

  const handleStoreSelect = (storeName: string) => {
    setSelectedStores(prev => {
      if (prev.includes(storeName)) {
        return prev.filter(name => name !== storeName);
      } else {
        return [...prev, storeName];
      }
    });
  };

  const handleSelectAllStores = (checked: boolean, currentStore: any) => {
    const selectableStores = storesList
      .filter((s: any) => s.friendly_name !== currentStore.friendly_name)
      .map((s: any) => s.friendly_name);

    if (checked) {
      setSelectedStores(prev => {
        const newSelection = [...prev];
        selectableStores.forEach((store: any) => {
          if (!newSelection.includes(store)) {
            newSelection.push(store);
          }
        });
        return newSelection;
      });
    } else {
      setSelectedStores(prev =>
        prev.filter(store => !selectableStores.includes(store))
      );
    }
  };

  const areAllStoresSelected = (currentStore: any) => {
    const selectableStores = storesList
      .filter((s: any) => s.friendly_name !== currentStore.friendly_name)
      .map((s: any) => s.friendly_name);

    return (
      selectableStores.length > 0 &&
      selectableStores.every((name: string) => selectedStores.includes(name))
    );
  };

  const checkAndUpdateCompletion = (stores: any[]) => {
    const hasQuestionsOrFiles = stores.some(store => {
      const hasQuestions = store.storeknowledgeBase?.qna?.length > 0;
      const hasFiles = store.storeknowledgeBase?.files?.length > 0;
      return hasQuestions || hasFiles;
    });

    dispatch(
      setStepStatus({
        step: 'faqSetup',
        status: {
          isComplete: hasQuestionsOrFiles,
          hasError: false,
        },
      })
    );
  };

  const handleSaveQuestionnaire = (
    questions: any[],
    targetStores?: string[]
  ) => {
    try {
      let stores;
      if (targetStores) {
        // When copying questions, filter out the source store and only include selected target stores
        stores = storesList.filter(
          (s: any) =>
            targetStores.includes(s.friendly_name) && s.id !== selectedStore.id // Exclude the source store
        );
      } else {
        // For new questions via QuestionnaireDrawer, use the selected store
        stores = [selectedStore];
      }

      // Only proceed if there are stores to copy to
      if (stores.length > 0) {
        const formattedData: IStoreKnowledgeRequest = {
          stores: stores.map((store: any) => ({
            storeId: store.id,
            storeAlias: store.friendly_name,
            knowledgeBase: {
              method: 'quesandAnswer',
              qna: questions.map((q, index) => ({
                id: (store.storeknowledgeBase?.qna?.length || 0) + index + 1,
                ques: q.ques || q.question,
                ans: q.ans || q.answer,
              })),
            },
          })),
        };

        createStoreKnowledge(formattedData, {
          onSuccess: () => {
            setIsQuestionnaireDrawerOpen(false);
            if (targetStores) {
              setSelectedStores([]); // Clear selected stores after copying
              showAlert('Questions copied successfully', 'success');
            }
            // Check completion status after successful save
            queryClient
              .invalidateQueries({
                queryKey: ['stores'],
              })
              .then(() => {
                if (storesList) {
                  checkAndUpdateCompletion(storesList);
                }
              });
          },
        });
      }
    } catch (error) {
      console.error('Error saving questionnaire:', error);
      showAlert('Error copying questions', 'error');
    }
  };

  const handleEditQuestion = (store: any, index: number, questionData: any) => {
    setSelectedStore(store);
    setQuestionIndex(index);
    setSelectedQuestion({
      id: questionData.id,
      question: questionData.ques,
      answer: questionData.ans,
    });
    setIsEditDrawerOpen(true);
  };

  const handleSaveEdit = (editedQuestion: {
    question: string;
    answer: string;
  }) => {
    editStoreKnowledge({
      storeId: selectedStore.id,
      id: selectedQuestion.id,
      question: editedQuestion.question,
      answer: editedQuestion.answer,
    });
    setSelectedQuestion({
      ...selectedQuestion,
      question: editedQuestion.question,
      answer: editedQuestion.answer,
    });
    setIsEditDrawerOpen(false);
  };

  const handleDeleteQuestion = (store: any, questionData: any) => {
    deleteStoreKnowledge({
      method: 'quesandAnswer',
      storeId: store.id,
      id: questionData.id,
    });
  };

  const handleDeleteFile = async (storeId: string, fileName: string) => {
    await deleteKnowledgeBaseFile(
      user?._id || '',
      storeId.toString(),
      fileName
    );
  };

  useEffect(() => {
    if (!storesListLoading && storesList) {
      checkAndUpdateCompletion(storesList);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesList, storesListLoading]);

  return (
    <>
      <NewFaqSetupUI
        storesListLoading={storesListLoading}
        storesList={storesList}
        isQuestionnaireDrawerOpen={isQuestionnaireDrawerOpen}
        selectedStore={selectedStore}
        selectedStores={selectedStores}
        isPending={isPending}
        onNext={onNext}
        onPrev={onPrev}
        onCreateQuestionnaire={handleCreateQuestionnaire}
        onCloseQuestionnaire={() => setIsQuestionnaireDrawerOpen(false)}
        onSaveQuestionnaire={handleSaveQuestionnaire}
        onStoreSelect={handleStoreSelect}
        onSelectAllStores={handleSelectAllStores}
        areAllStoresSelected={areAllStoresSelected}
        onEditQuestion={handleEditQuestion}
        onDeleteQuestion={handleDeleteQuestion}
        deletingQuestionIds={deletingQuestionIds}
        onDeletePending={isDeletePending}
        onDeleteFile={handleDeleteFile}
      />
      <QuestionEditDrawer
        open={isEditDrawerOpen}
        onClose={() => setIsEditDrawerOpen(false)}
        selectedStore={selectedStore}
        questionData={selectedQuestion}
        questionIndex={questionIndex}
        onSave={handleSaveEdit}
        isPending={isEditPending}
      />
    </>
  );
};

export default NewFaqSetup;
